/**
 * TREATX-PROVIDER-ACCOUNT-LIFECYCLE-01 — Public provider reactivation.
 * Step 1: enter registered email -> POST /api/provider/account/reactivate/request
 * Step 2: enter emailed code    -> POST /api/provider/account/reactivate/confirm
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  if (typeof detail.message === 'string') return detail.message;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : '')).filter(Boolean).join(' ') || fallback;
  }
  return fallback;
}

export default function ReactivateAccountPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post('/provider/account/reactivate/request', { email: email.trim() });
      toast.success(t('accountLifecycle.reactivateSent'));
      if (data?.dev_otp) {
        // Mock-email mode only — dev convenience for preview testing.
        setCode(String(data.dev_otp));
      }
      setStep(2);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail, t('accountLifecycle.reactivateError')));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post('/provider/account/reactivate/confirm', { email: email.trim(), code: code.trim() });
      toast.success(t('accountLifecycle.reactivateSuccess'));
      setTimeout(() => navigate('/login'), 900);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail, t('accountLifecycle.reactivateError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10" data-testid="reactivate-page">
      <Card className="w-full max-w-md">
        <CardContent className="py-8">
          <div className="flex items-center gap-2 mb-1">
            <RotateCcw className="h-5 w-5 text-blue-700" />
            <h1 className="text-lg font-semibold text-slate-900" data-testid="reactivate-title">
              {t('accountLifecycle.reactivateTitle')}
            </h1>
          </div>
          <p className="text-sm text-slate-500 mb-6">{t('accountLifecycle.reactivateSubtitle')}</p>

          {step === 1 ? (
            <form onSubmit={sendCode} className="space-y-4">
              <div>
                <Label className="text-xs text-slate-600">{t('accountLifecycle.reactivateEmailLabel')}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@clinic.com"
                  data-testid="reactivate-email"
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="reactivate-send-code">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('accountLifecycle.reactivateSendCode')}
              </Button>
            </form>
          ) : (
            <form onSubmit={confirm} className="space-y-4">
              <div>
                <Label className="text-xs text-slate-600">{t('accountLifecycle.reactivateCodeLabel')}</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t('accountLifecycle.reactivateCodePlaceholder')}
                  inputMode="numeric"
                  data-testid="reactivate-code"
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="reactivate-confirm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('accountLifecycle.reactivateConfirm')}
              </Button>
              <button
                type="button"
                onClick={sendCode}
                className="text-xs text-blue-700 hover:underline w-full text-center"
                data-testid="reactivate-resend"
              >
                {t('accountLifecycle.reactivateResend')}
              </button>
            </form>
          )}

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mt-6 text-xs text-slate-500 hover:underline w-full text-center"
            data-testid="reactivate-back-login"
          >
            {t('accountLifecycle.reactivateBack')}
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
